/** @format */

import { useToast } from '@fukuri-admins/ui/common/Toast/hooks';
import { useEffect, useRef, useState } from 'react';
import { PaginatedSelect } from '../../../../../src/component/PaginatedSelect';
import { ApiResponse, OptionType } from '../../../../../src/component/PaginatedSelect/type';
import { getClients } from '../../../api/clients';
import { NewClientAdminUserFormType } from '../../../types/forms/NewClientAdminUserFormType';
import { ClientType } from '../../../types/resources/ClientType';
import { validateEmail, validateRequired } from '../../../utils/validation';
import { useAuth } from '../../auth/AuthProvider';
import { ActionButton } from '../../common/ActionButton';
import { ActionInput } from '../../common/ActionInput';
import MessageModal from '../../common/MessageModal';
import styles from './styles.module.scss';

interface AddClientAdminUserModalProps {
  show: boolean;
  onClose: () => void;
  action: (data: NewClientAdminUserFormType) => Promise<boolean>;
  creditEvaluationFlag: boolean;
}

export default function AddClientAdminUserModal({
  show,
  onClose,
  action,
  creditEvaluationFlag,
}: AddClientAdminUserModalProps) {
  const { signOut } = useAuth();

  const initialFormData = {
    clientId: null,
    fullName: '',
    email: '',
  };

  const [showStatus, setShowStatus] = useState(show);
  // nullにすることでPaginatedSelect（react-select）が「制御モード・選択なし」として動作する。
  // undefinedだとreact-selectは非制御モードとみなし、内部状態（前回の選択値）を保持し続ける。
  // このモーダルはCSSで表示切替するためアンマウントされず、undefinedのままだと再表示時に選択が残ってしまう。
  const [selectedClient, setSelectedClient] = useState<OptionType | null>(null);
  const [formData, setFormData] = useState(initialFormData);
  const [errMsg, setErrMsg] = useState<string[]>([]);
  const [errorMessageModalStatus, setErrorMessageModalStatus] = useState(false);

  const rootRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const { showToast } = useToast();

  const resetFormData = () => {
    setFormData(initialFormData);
    setSelectedClient(null);
    setErrMsg([]);
  };

  const handleClose = () => {
    resetFormData();
    onClose();
  };

  useEffect(() => {
    document.body.style.overflow = show ? 'hidden' : 'auto';
    document.addEventListener('click', handleClick);
    return () => document.removeEventListener('click', handleClick);
  }, [show]);

  const handleClick = (e: MouseEvent) => {
    if (
      rootRef.current?.contains(e.target as Node) &&
      !contentRef.current?.contains(e.target as Node)
    ) {
      handleClose();
    }
  };

  useEffect(() => {
    setShowStatus(show);
  }, [show]);

  const validateForm = (data: NewClientAdminUserFormType): string[] => {
    return [
      validateRequired(data.client_id, '利用企業'),
      validateRequired(data.full_name, 'フルネーム'),
      validateEmail(data.email),
    ].filter(Boolean);
  };

  const handleNext = async () => {
    const inputProps: NewClientAdminUserFormType = {
      client_id: formData.clientId,
      full_name: formData.fullName,
      email: formData.email,
    };

    const errors = validateForm(inputProps);

    setErrMsg([]);
    if (errors.length > 0) {
      setErrMsg(errors);
      setErrorMessageModalStatus(true);
    } else {
      const success = await action(inputProps);
      if (success) {
        handleClose();
      }
    }
  };

  const updateFormData = (field: string, value: string | number | null) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const searchAction = async (input: string, page: number): Promise<ApiResponse> => {
    try {
      const { data, headers } = await getClients(signOut, {
        name: input,
        page: page.toString(),
        enabled: '1',
      });

      return {
        options: data.clients.map((client: ClientType) => ({
          label: client.name,
          value: client.id,
        })),
        headers: {
          'total-pages': headers['total-pages'],
        },
      };
    } catch {
      showToast({ message: 'データの取得に失敗しました。', status: 'danger' });
    }
  };

  return (
    <div
      className={showStatus ? styles.loaderWrapper : styles.displayNone}
      ref={rootRef}
      data-testid='add-client-admin-modal'
    >
      <div className={styles.modalRoot} ref={contentRef}>
        <div className={styles.title}>企業管理者の追加</div>
        <div className={styles.descContainer}>
          各項目に情報を入力し、追加ボタンを押してください。
          <br />
          {creditEvaluationFlag
            ? '企業の与信が完了している場合、管理画面のご案内メールが管理者様宛に送信されます。'
            : '管理者作成後、管理画面のご案内メールが管理者様宛に送信されます。'}
        </div>
        <div className={styles.mainContainer}>
          <div className={styles.errorMessage}>
            {errMsg.length > 0 && (
              <>
                <div>入力エラー</div>
                <ul>
                  {errMsg.map((item, key) => (
                    <li key={key}>{item}</li>
                  ))}
                </ul>
              </>
            )}
          </div>
          <div className={styles.form}>
            <div className={styles.formTitle}>
              利用企業 <span className={styles.symbol}>*</span>
            </div>
            <PaginatedSelect
              searchAction={searchAction}
              isClearable={true}
              value={selectedClient}
              onChange={e => {
                setSelectedClient(e);
                updateFormData('clientId', e?.value ?? null);
              }}
            />
          </div>
          <div className={styles.form}>
            <div className={styles.formTitle}>
              フルネーム <span className={styles.symbol}>*</span>
            </div>
            <ActionInput
              defaultValue={formData.fullName}
              action={e => updateFormData('fullName', e.target.value)}
              placeholder='山田'
              autoComplete='off'
              data-testid='name-input'
            />
          </div>
          <div className={styles.form}>
            <div className={styles.formTitle}>
              メールアドレス <span className={styles.symbol}>*</span>
            </div>
            <ActionInput
              defaultValue={formData.email}
              action={e => updateFormData('email', e.target.value)}
              placeholder='xxx@xxxx.xx.xx'
              className={styles.formMiddle}
              autoComplete='off'
              data-testid='email-input'
            />
          </div>
        </div>
        <div className={styles.btnContainer}>
          <ActionButton
            content='キャンセル'
            className={styles.cancelBtn}
            action={handleClose}
            data-testid='add-client-admin-cancel'
          />
          <ActionButton
            content='追加'
            type='dark'
            className={styles.confirmBtn}
            action={handleNext}
            data-testid='add-client-admin-submit'
          />
        </div>
        <MessageModal
          title='入力エラー'
          description='入力エラーがあります。画面上部に表示しているエラーメッセージをご確認ください。'
          show={errorMessageModalStatus}
          onClose={() => setErrorMessageModalStatus(false)}
          action={() => setErrorMessageModalStatus(false)}
          data-testid='error-message-modal'
        />
      </div>
    </div>
  );
}
